/**
 * NxChequeosSalud -- la tarjeta "Chequeos del sistema" arriba de la Sala de Maquinas (SALUD-PANEL-T1).
 *
 * AUDIENCIA: solo el equipo Nexo (#/salud ya es audiencia nexo), escritorio y celular.
 * Poco texto: una linea ("Todo en verde" o "1 en rojo: <frase>") y, al tocar, cada chequeo con su estado, la frase del
 * catalogo, el valor y su tendencia de las ultimas 25 corridas (24 anteriores + la ultima) como cuadritos de color.
 * Va aparte del tablero: si el tablero falla, los chequeos se pintan igual (y al reves). Sin la ruta no se pinta.
 *
 * @module presentation/shared/NxChequeosSalud
 */
import { SaludApiClient } from '../../infrastructure/api/SaludApiClient.js';
import { normalizarChequeos, ordenarChequeos, lineaResumen, estadoDe } from '../../application/salud/chequeosSalud.js';

const C = { texto: '#e6edf3', suave: '#8b949e', linea: '#30363d', fondo: '#161b22', ok: '#3fb950', ojo: '#d29922', mal: '#f85149', gris: '#8b949e', enlace: '#58a6ff' };
// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from './texto.js';
const hora = (h) => (h ? String(h).replace('T', ' ').slice(0, 16) : 'sin hora');

function tendencia(ch) {
  const puntos = [...ch.historial, { hora: ch.hora, estado: ch.estado, valor: ch.valor }];
  const cuadros = puntos.map((p) => {
    const e = estadoDe(p.estado);
    return `<span title="${esc(hora(p.hora))}: ${esc(e.texto)}${p.valor === null ? '' : ' (' + esc(p.valor) + ')'}" style="display:inline-block;width:8px;height:14px;border-radius:2px;background:${C[e.tono]}${p.estado === 'unknown' || !(p.estado in { ok: 1, warning: 1, critical: 1 }) ? '55' : ''}"></span>`;
  }).join('');
  const resumen = puntos.reduce((a, p) => { a[estadoDe(p.estado).texto] = (a[estadoDe(p.estado).texto] || 0) + 1; return a; }, {});
  const etiqueta = Object.entries(resumen).map(([k, n]) => `${n} ${k.toLowerCase()}`).join(', ');
  return `<div data-chq-tendencia role="img" aria-label="Ultimas ${puntos.length} corridas: ${esc(etiqueta)}" style="display:flex;gap:2px;flex-wrap:wrap;margin-top:6px">${cuadros}</div>`;
}

function chequeoHtml(ch) {
  const e = estadoDe(ch.estado);
  const valor = ch.valor === null ? '' : `<span style="color:${C.suave}">${esc(ch.valor)}${ch.unidad ? ' ' + esc(ch.unidad) : ''}</span>`;
  return `<li data-chq="${esc(ch.codigo)}" data-chq-estado="${esc(ch.estado)}" style="padding:8px 0;border-top:1px solid ${C.linea}">
    <div style="display:flex;flex-wrap:wrap;gap:2px 10px;align-items:baseline">
      <strong style="flex:1 1 180px;min-width:0;overflow-wrap:anywhere">${esc(ch.nombre)}</strong>
      <span style="white-space:nowrap;color:${C[e.tono]};font-weight:600">${esc(e.texto)}</span>
    </div>
    <div style="margin-top:2px">${esc(ch.frase || ch.motivo || 'Sin frase en el catalogo')}</div>
    <div style="margin-top:2px;display:flex;flex-wrap:wrap;gap:2px 10px">${valor}<span style="color:${C.suave}">${esc(ch.codigo)} v${esc(ch.version)} · ${esc(hora(ch.hora))}</span>${ch.incidenteAbierto ? `<span style="color:${C.mal}">incidente abierto</span>` : ''}</div>
    ${tendencia(ch)}
  </li>`;
}

/** Lo pintado, puro sobre lo leido (para el guardia). Sin respuesta: ''. */
export function chequeosHtml(items) {
  if (!items) return '';
  const lista = ordenarChequeos(items);
  const r = lineaResumen(lista);
  return `<style>
      [data-chequeos] summary { list-style: none; cursor: pointer; }
      [data-chequeos] summary::-webkit-details-marker { display: none; }
      [data-chequeos] summary::before { content: '\\25B8  '; color: ${C.suave}; }
      [data-chequeos] details[open] > summary::before { content: '\\25BE  '; }
      [data-chequeos] summary:focus-visible { outline: 2px solid ${C.enlace}; outline-offset: 2px; }
    </style>
    <section data-chequeos style="margin-top:14px;padding:12px 14px;border:1px solid ${C.linea};border-left:4px solid ${C[r.tono]};border-radius:10px;background:${C.fondo};font-size:14px;color:${C.texto}">
      <details>
        <summary><span data-chq-resumen style="font-weight:700;color:${C[r.tono]}">${esc(r.texto)}</span> <span style="color:${C.suave}">· chequeos del sistema</span></summary>
        <ul style="list-style:none;margin:8px 0 0;padding:0">${lista.map(chequeoHtml).join('')}</ul>
      </details>
    </section>`;
}

/** Monta la tarjeta en el host; sin la ruta deja el host vacio. @returns {Promise<boolean>} si pinto. */
export async function renderChequeosSalud($host) {
  if (!$host) return false;
  const r = await SaludApiClient.getChequeos();
  if (!$host.isConnected) return false;
  $host.innerHTML = r.ok ? chequeosHtml(normalizarChequeos(r.data)) : '';
  return r.ok;
}
